import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import Logo from "./Logo";
import {
    LayoutDashboard, Compass, BookOpen, Layers, ClipboardCheck,
    Award, FolderOpen, Mic, BarChart3, Settings2,
} from "lucide-react";

// Every possible nav item the platform supports, keyed by the same nav_keys
// the backend stores on each Group. The sidebar renders only items whose key
// is present in the union of nav_keys across the user's groups.
const NAV_ITEMS = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, key: "dashboard" },
    { to: "/start", label: "Start Here", icon: Compass, key: "start-here" },
    { to: "/modules", label: "Modules", icon: BookOpen, key: "modules" },
    { to: "/flashcards", label: "Flash Cards", icon: Layers, key: "flashcards" },
    { to: "/quizzes", label: "Quizzes", icon: ClipboardCheck, key: "quizzes" },
    { to: "/certifications", label: "Certifications", icon: Award, key: "certifications" },
    { to: "/resources", label: "Resources", icon: FolderOpen, key: "resources" },
    { to: "/roleplays", label: "Roleplay Library", icon: Mic, key: "roleplays" },
    { to: "/progress", label: "Progress", icon: BarChart3, key: "progress" },
];

export default function Sidebar({ onNavigate }) {
    const { user, logout, navVersion } = useAuth();
    const navigate = useNavigate();
    const isStaff = user?.role === "admin" || user?.role === "trainer";
    const [navFlags, setNavFlags] = useState(null);

    useEffect(() => {
        if (!user?.id) return;
        let cancelled = false;
        api.get("/me/nav")
            .then((r) => { if (!cancelled) setNavFlags(r.data || null); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [user?.id, user?.preview_groups?.length, navVersion]);

    // The backend returns both per-item booleans (whether to surface based on
    // content presence) and a `nav_keys` array (the union of group nav_keys).
    // An item is shown iff its key is in nav_keys AND its boolean is true.
    const isVisible = (item) => {
        if (!navFlags) return true; // first paint — show all, hide once flags arrive
        const keys = navFlags.nav_keys || [];
        if (!keys.includes(item.key)) return false;
        // Content-presence override (e.g. don't show "Flash Cards" if there are none)
        if (item.key === "flashcards") return navFlags.flashcards !== false;
        if (item.key === "quizzes") return navFlags.quizzes !== false;
        if (item.key === "resources") return navFlags.resources !== false;
        if (item.key === "roleplays") return navFlags.roleplays !== false;
        return true;
    };

    const navItems = NAV_ITEMS.filter(isVisible);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <aside
            className="w-64 shrink-0 h-screen sticky top-0 hidden lg:flex flex-col bg-ink-100 border-r border-ink-300"
            data-testid="sidebar"
        >
            <div className="px-6 pt-7 pb-6 border-b border-ink-300">
                <Logo tone="light" className="h-6" />
                <div className="mt-2 font-editorial italic text-base text-ink-500">University</div>
                <div className="mt-1 text-[10px] tracking-[0.2em] uppercase text-ink-400 font-mono">
                    Internal Training System
                </div>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-4 scrollbar-thin">
                <div className="px-3 pb-2 text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">
                    Workspace
                </div>
                {navItems.map((item) => (
                    <NavLink
                        key={item.key}
                        to={item.to}
                        end={item.end}
                        onClick={onNavigate}
                        data-testid={`nav-${item.key}`}
                        className={({ isActive }) =>
                            `group flex items-center gap-3 px-3 py-2 my-0.5 text-sm rounded-sm transition-all ${
                                isActive
                                    ? "bg-white text-ink-900 border border-ink-300 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
                                    : "text-ink-500 hover:text-ink-900 hover:bg-white/60"
                            }`
                        }
                    >
                        <item.icon size={16} strokeWidth={1.5} />
                        <span className="font-medium">{item.label}</span>
                    </NavLink>
                ))}

                {isStaff && (
                    <>
                        <div className="px-3 pt-6 pb-2 text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">
                            Operations
                        </div>
                        <NavLink
                            to="/admin"
                            onClick={onNavigate}
                            data-testid="nav-admin"
                            className={({ isActive }) =>
                                `group flex items-center gap-3 px-3 py-2 my-0.5 text-sm rounded-sm transition-all ${
                                    isActive
                                        ? "bg-white text-ink-900 border border-ink-300"
                                        : "text-ink-500 hover:text-ink-900 hover:bg-white/60"
                                }`
                            }
                        >
                            <Settings2 size={16} strokeWidth={1.5} />
                            <span className="font-medium">Admin</span>
                        </NavLink>
                    </>
                )}
            </nav>

            <div className="border-t border-ink-300 p-4">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-haevn-700 text-white flex items-center justify-center text-xs font-medium">
                        {user?.name?.[0]?.toUpperCase() ?? "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-ink-900 truncate" data-testid="sidebar-user-name">
                            {user?.name}
                        </div>
                        <div className="text-[11px] text-ink-500 capitalize tracking-wide">{user?.role}</div>
                    </div>
                </div>
                <button
                    onClick={handleLogout}
                    data-testid="sidebar-logout-btn"
                    className="mt-3 w-full text-xs text-ink-500 hover:text-ink-900 transition-colors text-left font-mono uppercase tracking-[0.15em]"
                >
                    Sign out
                </button>
            </div>
        </aside>
    );
}
